import { useEffect, useMemo, useState } from "react"
import Button from "../../../ui/Button"
import ProcessingState from "../../../ui/Admin/ProcessingState"
import InputFieldWithLabel from "../../Inputs/InputFieldWithLabel"
import UserModalContent from "./components/Modals/UserModalContent"
import { IAdminUser } from "./interfaces/AdminUserInterface"
import { useUser } from "./hooks/useUser"
import Spinner from "~/components/ui/Spinner"
import { getGridTemplateColumns } from "./helpers/GridTemplateConstant"
import _ from "lodash"
import UserTableRow from "./components/UserTableRow"
import { useCamp } from "~/api2/hooks/useCamp"
import { useModal } from "~/contexts/ModalProvider"
import { useMeCamps } from "~/api2/hooks/useMeCamps"

const Users = () => {
  const [users, setUsers] = useState<IAdminUser[] | null>(null)
  const [searchInput, setSearchInput] = useState<string>("")
  const { openModal, closeModal } = useModal()

  const { data: camps } = useMeCamps()

  const accessibleCamps = camps?.camps

  const {
    index: { data: camp },
  } = useCamp()

  const { resource: fetchedUsers, isLoading } = useUser({ camp_id: camp.id })

  const handleUserClick = (selectedUser?: IAdminUser) => {
    openModal("generic", {
      children: <UserModalContent selectedUser={selectedUser} setUsers={setUsers} closeModal={closeModal} />,

      onClose: () => {
        closeModal()
      },
    })
  }

  const renderAuthorizedUsers = () => {
    let filteredUsers = users?.filter((user) => user.invitation_accepted_at?.length)

    if (searchInput.length) {
      return filteredUsers?.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.last_name?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.role?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.camps.map((camp) => camp.name?.toLowerCase()).includes(searchInput?.toLowerCase()),
      )
    }

    return filteredUsers || []
  }

  const renderUnauthorizedUsers = () => {
    let filteredUsers = users?.filter((user) => !user.invitation_accepted_at?.length)

    if (searchInput.length) {
      return filteredUsers?.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.last_name?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.role?.toLowerCase().includes(searchInput?.toLowerCase()) ||
          user.camps.map((camp) => camp.name?.toLowerCase()).includes(searchInput?.toLowerCase()),
      )
    }

    return filteredUsers || []
  }

  const memoizedAuthorizedUsers = useMemo(() => renderAuthorizedUsers(), [searchInput, users])
  const memoizedUnauthorizedUsers = useMemo(() => renderUnauthorizedUsers(), [searchInput, users])

  const isIdenticalCamps = (accessibleCamps?.length || 0) <= 1

  useEffect(() => {
    if (!!fetchedUsers && !_.isEqual(fetchedUsers, users)) {
      setUsers(fetchedUsers)
    }
  }, [fetchedUsers])

  if (!accessibleCamps) return

  return (
    <div className="relative">
      <div className="flex justify-between wrap gap-3 items-center flex-row">
        <div className="my-6 space-y-2">
          <div className="text-xl font-semibold text-gray-900">User Management</div>
          <div className="font-medium text-gray-500">
            Add, edit and remove users, as well as manage access and roles
          </div>
        </div>
        <Button
          className="whitespace-nowrap w-32 xs:w-40"
          variant="blue"
          onClick={(e) => {
            e.preventDefault()
            handleUserClick()
          }}
        >
          Add New User
        </Button>
      </div>
      <InputFieldWithLabel
        className="py-1"
        placeholder="Search user..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />

      <div className="overflow-y-auto">
        {!![...memoizedAuthorizedUsers, ...memoizedUnauthorizedUsers].length && (
          <div
            className="grid"
            style={{ gridTemplateColumns: getGridTemplateColumns(!!isIdenticalCamps), minWidth: 840 }}
          >
            <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Email</div>
            <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Name</div>
            <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Role</div>
            {!isIdenticalCamps && (
              <div className="font-semibold text-md text-gray-900 py-5 whitespace-nowrap px-2">Camps</div>
            )}
            <div className=""></div>
          </div>
        )}
        {users?.length ? (
          <>
            <div className="">
              {!!memoizedAuthorizedUsers?.length &&
                memoizedAuthorizedUsers
                  .filter((user) => user.invitation_accepted_at?.length)
                  .map((user) => (
                    <UserTableRow user={user} handleUserClick={handleUserClick} isIdenticalCamps={isIdenticalCamps} />
                  ))}
            </div>
            {!!memoizedUnauthorizedUsers?.length && <div className="font-bold py-5 px-2">Pending invites</div>}
            <div className="">
              {!!memoizedUnauthorizedUsers?.length &&
                memoizedUnauthorizedUsers
                  .filter((user) => !user.invitation_accepted_at?.length)
                  .map((user) => (
                    <UserTableRow user={user} handleUserClick={handleUserClick} isIdenticalCamps={isIdenticalCamps} />
                  ))}
            </div>
          </>
        ) : isLoading ? (
          <div className="fixed z-50 inset-0 bg-black bg-opacity-35 flex items-center justify-center">
            <Spinner size={12} />
          </div>
        ) : (
          <div className="font-medium text-gray-500 text-2xl text-center mt-5">No users available...</div>
        )}
      </div>
      {isLoading && <ProcessingState loadingMessages={[]} successMessage={""} />}
    </div>
  )
}

export default Users
